/**
 * What has happened to this document, and what it said at the time.
 *
 * A PANEL, NOT A PAGE, because a history is read ALONGSIDE the document: the question is always
 * "what changed", and answering it in a separate screen makes somebody hold two states in their
 * head. Selecting a version replaces the page beside it with that text, read-only, and the editor
 * comes back when the panel closes.
 *
 * EVERY EVENT IS LISTED, including the saves that kept no text. The trail is complete and the list
 * must not imply otherwise by showing only what it can open -- those entries are simply not
 * clickable, and say so. The alternative, quietly hiding them, would make a document look as though
 * it had been touched six times when it was touched two hundred.
 *
 * NAMED VERSIONS ARE THE NAVIGATION. A list of times answers "when did this change" and never
 * "which of these went to review" -- the question somebody actually arrives with. A name is the
 * only durable handle a history has, so naming is offered on the current text (which writes a
 * snapshot, rather than hoping one fell nearby) and on any earlier moment whose text was kept, and
 * "Only named" collapses a thousand saves to the half-dozen anybody cites.
 *
 * READING IS NOT GATED THE WAY WRITING IS. Somebody who may see the document may see what has
 * happened to it -- that is most of what an inspection IS -- and a closed revision is exactly when
 * the history matters most. Restoring is gated, because restoring is an edit.
 */
import React from "react";
import {
    Alert, Box, Button, CircularProgress, Divider, IconButton, TextField, Tooltip, Typography, Slider,
} from "@mui/material";

import { makeCresAxios } from "../../util/CresAxios";
import useAppContext from "../../util/AppContext";
import Symbol from "./Symbol";
import { CHROME_FONT } from "./EditorToolbar";
import { colourFor } from "./suggestions";
import { groupVersions, foldedLabel, netChange } from "./versionGroups";
import { landmarksFor, positionOf, stopsFor } from "./timeSlider";
import { diffWords, plainText, TOO_LARGE, DiffPart } from "./textDiff";
import { Grey3, Grey5, Grey17, Blue3 } from "../../theme/styles";

export type Version = {
    USID: string;
    Action: string;
    Characters: number | null;
    Changed: boolean;
    CanView: boolean;
    ByUSID: string | null;
    ByName: string | null;
    At: string;
    /** What somebody called this moment, if anybody has. */
    Name?: string | null;
    NamedBy?: string | null;
    NamedAt?: string | null;
    /** Set when this moment was rebuilt from the operation log rather than read from a snapshot. */
    Rebuilt?: boolean;
};

/** The verbs, in the words a reader uses rather than the ones the column stores. */
const ACTIONS: Record<string, string> = {
    SAVED: "Edited",
    CREATED: "Created",
    IMPORTED: "Imported from Word",
    RESTORED: "Restored an earlier version",
    RENAMED: "Renamed",
    DELETED: "Deleted",
    SUGGESTION_ACCEPTED: "Accepted a suggestion",
    SUGGESTION_REJECTED: "Rejected a suggestion",
    RECONCILED: "Confirmed the imported structure",
    NAMED: "Named this version",
};

function when(at: string): string {
    const date = new Date(at);
    const today = new Date();
    const sameDay = date.toDateString() === today.toDateString();
    return sameDay
        ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : date.toLocaleString([], { month: "short", day: "numeric",
                                    hour: "numeric", minute: "2-digit" });
}

export default function VersionHistory({ documentUSID, editable, replayAt, viewing, viewingBody,
                                         onCopy, onView, onRestore, onClose }: {
    documentUSID: string;
    editable: boolean;
    /** Rebuild the document as it stood at a moment, from the operation log. */
    replayAt?: (documentUSID: string, at?: string) => Promise<any | null>;
    /** The version being previewed, if any, and the body being shown for it. */
    viewing: Version | null;
    viewingBody?: any;
    /** Put this version's text into a new part, rather than over the current one. */
    onCopy?: (name: string, body: any) => Promise<any>;
    onView: (version: Version | null, body: any) => void;
    onRestore: (version: Version, body: any) => void;
    onClose: () => void;
}) {
    const sessionContext = useAppContext()?.sessionContext as any;
    const axios = React.useMemo(() => makeCresAxios(sessionContext), [sessionContext]);

    const [versions, setVersions] = React.useState<Version[] | null>(null);
    const [problem, setProblem] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState<string | null>(null);
    const [namedOnly, setNamedOnly] = React.useState(false);
    /** Where the handle is while it is being dragged; null when it is following the viewed moment. */
    const [dragging, setDragging] = React.useState<number | null>(null);

    // DERIVED, NOT STORED. The stops are a reading of the version list; keeping a second copy in
    // state would be one more thing to invalidate every time the list reloads.
    const stops = React.useMemo(() => stopsFor(versions), [versions]);
    const landmarks = React.useMemo(() => landmarksFor(stops), [stops]);
    /** Which row is being named right now, and the text so far. "" means the current version. */
    const [naming, setNaming] = React.useState<{ usid: string; text: string } | null>(null);
    const [reload, setReload] = React.useState(0);
    /** Which folded runs the reader has opened. Collapsed again on reload, like every list. */
    const [unfolded, setUnfolded] = React.useState<Set<string>>(new Set());
    /** What changed in the version being viewed, against the one before it. Null until asked for. */
    const [changes, setChanges] = React.useState<DiffPart[] | typeof TOO_LARGE | null>(null);

    React.useEffect(() => {
        (async () => {
            try {
                const response = await axios.get(
                    `/t11/v1/document_history?USID=${encodeURIComponent(documentUSID)}&Limit=200`
                    + (namedOnly ? "&NamedOnly=true" : ""));
                setVersions(response?.data?.Results ?? []);
            } catch (error: any) {
                setProblem(error?.response?.data?.Cause || "The history could not be loaded.");
                setVersions([]);
            }
        })();
    }, [axios, documentUSID, namedOnly, reload]);

    /**
     * Give a moment a name, or take one away.
     *
     * AN EMPTY NAME REMOVES ONE, which is why this is not two functions. Naming the CURRENT text
     * (usid "") writes a new event carrying the body; naming an earlier one labels what is there.
     */
    const name = async (usid: string, text: string, version?: Version) => {
        setBusy(usid || "current");
        try {
            // A MOMENT WITH NO KEPT TEXT IS REBUILT HERE AND SENT WITH THE NAME. Replay belongs in
            // the browser -- it is where Yjs lives -- and without a body the server would have a
            // label on a row nobody could open, which it refuses for exactly that reason.
            let rebuilt: any = undefined;
            if (usid && version && !version.CanView && replayAt) {
                rebuilt = await replayAt(documentUSID, version.At);
                if (!rebuilt) {
                    setProblem("That moment is from before this document's edits began being "
                               + "recorded, so there is nothing to attach a name to.");
                    return;
                }
            }
            await axios.post("/t11/v1/document_history", usid
                ? { VersionUSID: usid, Name: text, Body: rebuilt }
                : { USID: documentUSID, Name: text });
            setNaming(null);
            setProblem(null);
            setReload(n => n + 1);
        } catch (error: any) {
            // THE SERVER'S OWN SENTENCE: "No text was kept for that moment, so it cannot be named"
            // says something nothing invented here could.
            setProblem(error?.response?.data?.Cause || "That version could not be named.");
        } finally {
            setBusy(null);
        }
    };

    /**
     * Show what the document said at this moment.
     *
     * THE KEPT SNAPSHOT FIRST, because it is one row and exactly right. Failing that, REBUILD the
     * moment from the operation log -- which is what makes every entry openable rather than only
     * the ones a five-minute snapshot happened to catch. A rebuild that cannot be sure returns
     * null and is reported, never approximated: a partial replay renders a document that never
     * existed.
     */
    const open = async (version: Version) => {
        setBusy(version.USID);
        try {
            if (!version.CanView) {
                const rebuilt = replayAt ? await replayAt(documentUSID, version.At) : null;
                if (!rebuilt) {
                    setProblem("No text was kept for that moment, and it is from before this "
                               + "document's edits began being recorded.");
                    return;
                }
                onView({ ...version, Rebuilt: true }, rebuilt);
                setProblem(null);
                return;
            }
            const got = await axios.get(
                `/t11/v1/document_history?VersionUSID=${encodeURIComponent(version.USID)}`);
            onView(version, got?.data?.Body);
            setProblem(null);
        } catch (error: any) {
            // THE SERVER'S OWN SENTENCE. "No text was kept for that moment" explains the gap in a
            // way nothing invented here could, and it is the one thing a reader needs to be told.
            setProblem(error?.response?.data?.Cause || "That version could not be opened.");
        } finally {
            setBusy(null);
        }
    };

    const restore = async () => {
        if (!viewing) return;
        setBusy(viewing.USID);
        try {
            const got = await axios.get(
                `/t11/v1/document_history?VersionUSID=${encodeURIComponent(viewing.USID)}`);
            onRestore(viewing, got?.data?.Body);
        } catch (error: any) {
            setProblem(error?.response?.data?.Cause || "That version could not be restored.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box sx={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${Grey3}`,
                   display: "flex", flexDirection: "column", fontFamily: CHROME_FONT,
                   maxHeight: "calc(100vh - 160px)", position: "sticky", top: 8 }}>

            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 15, color: Grey17, fontFamily: CHROME_FONT, flexGrow: 1 }}>
                    Version history
                </Typography>
                <Tooltip title={namedOnly ? "Showing only named versions" : "Show only named versions"}>
                    <IconButton size="small" onClick={() => setNamedOnly(v => !v)}
                                aria-label="Show only named versions"
                                aria-pressed={namedOnly}>
                        <Symbol name={namedOnly ? "bookmark" : "bookmark_border"} size={18}
                                colour={namedOnly ? Blue3 : Grey5}/>
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} aria-label="Close version history">
                    <Symbol name="close" size={18}/>
                </IconButton>
            </Box>
            <Divider/>

            {/* THE SLIDER. One stop per recorded moment, oldest on the left, the live document on
                the right -- see timeSlider.ts for why it is not mapped to elapsed time.

                IT DRIVES open(), the same function a row click does, so a moment reached by
                dragging is the same preview as one reached by clicking: the same read-only banner,
                the same Restore, the same Show changes, and the same refusal when no text was kept
                and the edits predate the log.

                ONLY WHEN THERE IS SOMEWHERE TO GO. One moment and "now" is not a history worth a
                control, and an inert slider reads as a broken one. */}
            {stops.length > 2 && (
                <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                    <Slider
                        size="small"
                        min={0} max={stops.length - 1}
                        value={dragging ?? positionOf(stops, viewing?.USID)}
                        marks={landmarks}
                        aria-label="Move through this document's history"
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value: number) => {
                            const stop = stops[value];
                            return stop?.version ? when(stop.version.At) : "Now";
                        }}
                        // WHILE DRAGGING, NOTHING IS FETCHED. Each stop is a replay -- a request and
                        // a CRDT rebuild -- so scrubbing across a week of history would fire one per
                        // moment passed. The handle and its label follow the finger; the document
                        // changes when the finger stops.
                        onChange={(_, value) => setDragging(value as number)}
                        onChangeCommitted={async (_, value) => {
                            const stop = stops[value as number];
                            setDragging(null);
                            if (!stop || stop.version?.USID === viewing?.USID) return;
                            // THE RIGHT-HAND END IS THE LIVE DOCUMENT, not a preview of the newest
                            // moment: dragging to the end means "put me back", and leaving the
                            // reader in a read-only copy of the current text would be a trap.
                            if (!stop.version) { onView(null, null); setProblem(null); return; }
                            await open(stop.version);
                        }}
                        disabled={!!busy}
                        sx={{ "& .MuiSlider-markLabel": { fontSize: 10, fontFamily: CHROME_FONT } }}
                    />
                    <Typography sx={{ fontSize: 11, color: Grey5, fontFamily: CHROME_FONT, mt: -0.5 }}>
                        {(() => {
                            const stop = stops[dragging ?? positionOf(stops, viewing?.USID)];
                            return stop?.version
                                ? `${when(stop.version.At)}${stop.version.VersionName
                                    ? ` — ${stop.version.VersionName}` : ""}`
                                : "The document as it stands now";
                        })()}
                    </Typography>
                </Box>
            )}

            {/* NAME THE CURRENT TEXT. Offered first because it is the thing somebody came to do
                before an approval -- and it writes a snapshot rather than labelling whichever
                five-minute one happened to fall nearby. */}
            {editable && (naming?.usid === "" ? (
                <Box sx={{ px: 1.5, py: 1, display: "flex", gap: 0.75, alignItems: "center" }}>
                    <TextField autoFocus size="small" fullWidth variant="standard"
                               placeholder="Issued for review"
                               value={naming.text}
                               slotProps={{ htmlInput: { maxLength: 120,
                                   style: { fontSize: 13, fontFamily: CHROME_FONT } } }}
                               onChange={e => setNaming({ usid: "", text: e.target.value })}
                               onKeyDown={e => {
                                   if (e.key === "Enter" && naming.text.trim()) name("", naming.text.trim());
                                   if (e.key === "Escape") setNaming(null);
                               }}/>
                    <Button size="small" disabled={!naming.text.trim() || !!busy}
                            onClick={() => name("", naming.text.trim())}
                            sx={{ fontSize: 12, fontFamily: CHROME_FONT }}>Save</Button>
                </Box>
            ) : (
                <Button size="small" startIcon={<Symbol name="bookmark_add" size={16}/>}
                        onClick={() => setNaming({ usid: "", text: "" })}
                        sx={{ m: 0.75, justifyContent: "flex-start", fontSize: 12.5,
                              color: Grey17, fontFamily: CHROME_FONT }}>
                    Name current version
                </Button>
            ))}

            {viewing && (
                <Box sx={{ px: 1.5, py: 1, backgroundColor: "#FEF7E0" }}>
                    <Typography sx={{ fontSize: 12, color: Grey17, fontFamily: CHROME_FONT }}>
                        Showing {when(viewing.At)}{viewing.Rebuilt ? ", rebuilt from the recorded edits" : ""}.
                        This is read-only.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                        {/* RESTORING IS AN EDIT and is offered only when the revision is open. It
                            goes through the ordinary save, so it asks t1, passes the digest fence
                            and lands in this history as an event of its own. */}
                        <Button size="small" variant="contained" disabled={!editable || !!busy}
                                onClick={restore} sx={{ fontSize: 12, fontFamily: CHROME_FONT }}>
                            Restore this version
                        </Button>
                        {/* WHAT CHANGED, against the version before this one. Worked out here and
                            stored nowhere: the trail is the record, this is a reading aid. */}
                        <Button size="small" disabled={!!busy}
                                onClick={async () => {
                                    if (changes) { setChanges(null); return; }
                                    const ordered = versions ?? [];
                                    const at = ordered.findIndex(one => one.USID === viewing.USID);
                                    const earlier = ordered[at + 1];
                                    if (!earlier) {
                                        setProblem("This is the earliest moment recorded, so there "
                                                   + "is nothing before it to compare with.");
                                        return;
                                    }
                                    setBusy(viewing.USID);
                                    try {
                                        const before = earlier.CanView
                                            ? (await axios.get("/t11/v1/document_history?VersionUSID="
                                                + encodeURIComponent(earlier.USID)))?.data?.Body
                                            : replayAt ? await replayAt(documentUSID, earlier.At) : null;
                                        if (!before) {
                                            setProblem("The version before this one cannot be read "
                                                       + "back, so there is nothing to compare with.");
                                            return;
                                        }
                                        setChanges(diffWords(plainText(before), plainText(viewingBody)));
                                    } finally {
                                        setBusy(null);
                                    }
                                }}
                                sx={{ fontSize: 12, color: Grey17, fontFamily: CHROME_FONT }}>
                            {changes ? "Hide changes" : "Show changes"}
                        </Button>
                        {/* COPY RATHER THAN RESTORE. Restoring makes this text the current text;
                            copying leaves the document alone and puts the old words somewhere you
                            can work on them -- which is what somebody drafting the next revision
                            from an old one actually wants, and what they reach for Restore to do
                            and regret. */}
                        {onCopy && editable && (
                            <Button size="small" disabled={!!busy}
                                    onClick={async () => {
                                        if (!viewingBody) return;
                                        const suggested = viewing.Name
                                            ? `${viewing.Name} (copy)`
                                            : `Copy of ${when(viewing.At)}`;
                                        const name = window.prompt("Name the new part", suggested);
                                        if (!name || !name.trim()) return;
                                        setBusy(viewing.USID);
                                        try {
                                            await onCopy(name.trim(), viewingBody);
                                        } catch (error: any) {
                                            setProblem(error?.response?.data?.Cause
                                                       || "That version could not be copied.");
                                        } finally {
                                            setBusy(null);
                                        }
                                    }}
                                    sx={{ fontSize: 12, color: Grey17, fontFamily: CHROME_FONT }}>
                                Copy to a new part
                            </Button>
                        )}
                        <Button size="small" onClick={() => { setChanges(null); onView(null, null); }}
                                sx={{ fontSize: 12, color: Grey17, fontFamily: CHROME_FONT }}>
                            Back to the current text
                        </Button>
                    </Box>
                </Box>
            )}

            {changes && (
                <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${Grey3}`,
                           maxHeight: 220, overflowY: "auto" }}>
                    {changes === TOO_LARGE ? (
                        <Typography sx={{ fontSize: 12.5, color: Grey5, fontFamily: CHROME_FONT }}>
                            These two versions are too long to compare in the browser. Open them
                            side by side instead.
                        </Typography>
                    ) : changes.every(part => part.kind === "same") ? (
                        <Typography sx={{ fontSize: 12.5, color: Grey5, fontFamily: CHROME_FONT }}>
                            No words changed. The save recorded something else — formatting, or two
                            windows settling.
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 12.5, color: Grey17, fontFamily: CHROME_FONT,
                                          whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                            {changes.map((part, at) => (
                                <Box key={at} component="span" sx={
                                    part.kind === "added"
                                        ? { backgroundColor: "#E6F4EA", color: "#137333" }
                                        : part.kind === "removed"
                                        ? { backgroundColor: "#FCE8E6", color: "#B3261E",
                                            textDecoration: "line-through" }
                                        // UNCHANGED TEXT IS DIMMED RATHER THAN HIDDEN: the changes
                                        // need their sentences around them or a reader cannot tell
                                        // what was changed FROM.
                                        : { color: Grey5 }
                                }>{part.text}</Box>
                            ))}
                        </Typography>
                    )}
                </Box>
            )}

            {problem && <Alert severity="info" sx={{ m: 1 }} onClose={() => setProblem(null)}>{problem}</Alert>}

            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
                {versions === null ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress size={20}/></Box>
                ) : versions.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: Grey5, p: 2, fontFamily: CHROME_FONT }}>
                        {namedOnly
                            ? "No version has been named yet. Name one before it goes to review, and it will be here."
                            : "Nothing has been recorded yet. The first save starts the history."}
                    </Typography>
                ) : groupVersions(versions).flatMap(group => {
                    // A FOLDED RUN IS ONE ROW UNTIL SOMEBODY OPENS IT, and then it is the run's
                    // own rows beneath a header. Nothing is hidden: the header says how many.
                    const expanded = unfolded.has(group.head.USID);
                    const rows = group.folded && !expanded ? [group.head] : group.members;
                    return [
                        ...(group.folded ? [(
                            <Box key={`fold-${group.head.USID}`}
                                 role="button" tabIndex={0}
                                 aria-expanded={expanded}
                                 aria-label={foldedLabel(group, when)}
                                 onClick={() => setUnfolded(was => {
                                     const next = new Set(was);
                                     if (next.has(group.head.USID)) next.delete(group.head.USID);
                                     else next.add(group.head.USID);
                                     return next;
                                 })}
                                 sx={{ px: 1.5, py: 0.75, cursor: "pointer",
                                       borderBottom: "1px solid #F1F3F4",
                                       display: "flex", alignItems: "center", gap: 0.75,
                                       "&:hover": { backgroundColor: "#F8F9FA" } }}>
                                <Symbol name={expanded ? "expand_more" : "chevron_right"} size={16}
                                        colour={Grey5}/>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                           backgroundColor: colourFor(group.head.ByUSID || "") }}/>
                                <Typography sx={{ fontSize: 12.5, color: Grey17,
                                                  fontFamily: CHROME_FONT }}>
                                    {foldedLabel(group, when)}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }}/>
                                {netChange(group) != null && netChange(group) !== 0 && (
                                    <Typography sx={{ fontSize: 11, color: Grey5,
                                                      fontFamily: CHROME_FONT }}>
                                        {(netChange(group) as number) > 0 ? "+" : ""}
                                        {(netChange(group) as number).toLocaleString()}
                                    </Typography>
                                )}
                            </Box>
                        )] : []),
                        ...rows.map(version => {
                    const here = viewing?.USID === version.USID;
                    return (
                        <Box key={version.USID}
                             role="button"
                             tabIndex={0}
                             aria-label={`Open the version from ${when(version.At)}`}
                             onClick={() => open(version)}
                             sx={{ px: 1.5, py: 1, borderBottom: `1px solid #F1F3F4`,
                                   pl: group.folded ? 3.5 : 1.5,
                                   cursor: "pointer",
                                   backgroundColor: here ? "#E8F0FE" : "transparent",
                                   "&:hover": { backgroundColor: here ? "#E8F0FE" : "#F8F9FA" } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                           backgroundColor: colourFor(version.ByUSID || "") }}/>
                                {/* THE NAME IS THE LABEL when there is one, and the time becomes
                                    the subtitle: somebody scanning for "Issued for review" should
                                    not have to read timestamps to find it. */}
                                <Typography sx={{ fontSize: 13, color: Grey17,
                                                  fontWeight: version.Name ? 600 : here ? 600 : 400,
                                                  fontFamily: CHROME_FONT, minWidth: 0,
                                                  overflow: "hidden", textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap" }}>
                                    {version.Name || when(version.At)}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }}/>
                                {editable && (
                                    <Tooltip title={version.Name ? "Rename or remove the name" : "Name this version"}>
                                        <IconButton size="small" aria-label="Name this version"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setNaming({ usid: version.USID, text: version.Name || "" });
                                                    }}>
                                            <Symbol name={version.Name ? "bookmark" : "bookmark_border"}
                                                    size={15} colour={version.Name ? Blue3 : Grey5}/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {version.CanView
                                    ? <Tooltip title="This moment's text was kept">
                                          <Box component="span"><Symbol name="history" size={15}
                                               colour={here ? Blue3 : Grey5}/></Box>
                                      </Tooltip>
                                    /* NO SNAPSHOT, BUT STILL OPENABLE: the moment is rebuilt from
                                       the operation log. The distinction is kept on screen because
                                       it is real -- a rebuilt moment is reconstructed from the
                                       edits, a kept one is the text as it was stored. */
                                    : <Tooltip title="Rebuilt from the recorded edits">
                                          <Box component="span"><Symbol name="restore_page" size={15}
                                               colour={here ? Blue3 : Grey5}/></Box>
                                      </Tooltip>}
                            </Box>
                            {naming?.usid === version.USID ? (
                                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", ml: 2, mt: 0.5 }}
                                     onClick={e => e.stopPropagation()}>
                                    <TextField autoFocus size="small" fullWidth variant="standard"
                                               placeholder="Issued for review"
                                               value={naming.text}
                                               slotProps={{ htmlInput: { maxLength: 120,
                                                   style: { fontSize: 12.5, fontFamily: CHROME_FONT } } }}
                                               onChange={e => setNaming({ usid: version.USID, text: e.target.value })}
                                               onKeyDown={e => {
                                                   if (e.key === "Enter") name(version.USID, naming.text.trim(), version);
                                                   if (e.key === "Escape") setNaming(null);
                                               }}/>
                                    {/* EMPTY REMOVES THE NAME -- the event itself is untouched. */}
                                    <Button size="small" disabled={!!busy}
                                            onClick={() => name(version.USID, naming.text.trim(), version)}
                                            sx={{ fontSize: 12, fontFamily: CHROME_FONT }}>
                                        {naming.text.trim() ? "Save" : "Remove"}
                                    </Button>
                                </Box>
                            ) : null}
                            <Typography sx={{ fontSize: 12, color: Grey5, ml: 2, fontFamily: CHROME_FONT }}>
                                {version.Name ? `${when(version.At)} · ` : ""}
                                {version.Action === "NAMED" && !version.Name
                                    ? "Named this version · name since removed"
                                    : ACTIONS[version.Action] ?? version.Action}
                                {version.ByName ? ` · ${version.ByName}` : ""}
                                {version.Characters != null ? ` · ${version.Characters.toLocaleString()} chars` : ""}
                                {!version.Changed && version.Action !== "NAMED" ? " · no change" : ""}
                                {version.Name && version.NamedBy && version.NamedBy !== version.ByName
                                    ? ` · named by ${version.NamedBy}` : ""}
                            </Typography>
                        </Box>
                    );
                        }),
                    ];
                })}
            </Box>
        </Box>
    );
}
